import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Container,
} from "reactstrap";
import { Puff } from "react-loader-spinner";
import { ConnectedStore } from "models/ConnectedStore";
import { AdminService } from "services/AdminService";
import { toast } from "react-toastify";
import StoreServicesTab from "./StoreServicesTab";
import PlatformBadge from "./PlatformBadge";

const errorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "object" && error !== null) {
    const details = error as {
      message?: unknown;
      response?: { data?: { message?: unknown } };
    };
    const apiMessage = details.response?.data?.message;
    if (typeof apiMessage === "string" && apiMessage.trim()) {
      return apiMessage;
    }
    if (typeof details.message === "string" && details.message.trim()) {
      return details.message;
    }
  }
  return fallback;
};

const DEFAULT_TAB = "pricing";

const EditStore: React.FC = () => {
  const { integrationId, tab } = useParams<{
    integrationId: string;
    tab?: string;
  }>();
  const navigate = useNavigate();
  const [store, setStore] = useState<ConnectedStore | null>(null);
  const [loading, setLoading] = useState(true);
  const loadStore = useCallback(async () => {
    if (!integrationId) return;
    setLoading(true);
    try {
      const response = await AdminService.getConnectedShopifyStore(
        integrationId
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || "Unable to load store.");
      }
      setStore(response.data);
    } catch (error: unknown) {
      setStore(null);
      toast.error(errorMessage(error, "Unable to load connected store."));
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    document.title = "Edit Store | Zulu Courier";
    loadStore();
  }, [loadStore]);

  useEffect(() => {
    if (integrationId && tab && tab !== DEFAULT_TAB) {
      navigate(`/super_admin/stores/${integrationId}/${DEFAULT_TAB}`, {
        replace: true,
      });
    }
  }, [integrationId, navigate, tab]);

  if (loading) {
    return (
      <div className="page-content">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "500px" }}
        >
          <Puff color="#FFD0B8" height={50} width={50} />
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">Connected store could not be found.</Alert>
          <Button color="primary" onClick={() => navigate("/super_admin/stores")}>
            Back to Stores
          </Button>
        </Container>
      </div>
    );
  }

  const pricingActive = store.pricingStatus === "active";

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
          <div className="d-flex align-items-center">
            <Button
              color="light"
              className="me-3"
              onClick={() => navigate("/super_admin/stores")}
            >
              <i className="bx bx-left-arrow-alt font-size-20" />
            </Button>
            <div>
              <h4 className="mb-1">Edit Store</h4>
              <div className="text-muted">{store.storeName}</div>
            </div>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Badge color={pricingActive ? "success" : "warning"} pill>
              {pricingActive ? "Pricing Active" : "Pending Pricing"}
            </Badge>
            <PlatformBadge platform={store.platform} />
            <Badge color="success" pill>
              {store.connectionStatus}
            </Badge>
          </div>
        </div>

        <Card>
          <CardBody>
            <div className="store-settings-panel">
              <StoreServicesTab integrationId={integrationId || ""} />
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default EditStore;
